import { ArrowUpRight, Filter, AlertOctagon } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import EditorialBanner from "@/components/EditorialBanner";

const TwoProblems = () => {
  return (
    <section
      id="two-problems"
      data-testid="two-problems-section"
      className="light-section py-24 lg:py-32 relative overflow-hidden"
    >
      <div className="aurora-soft" />
      <div className="relative max-w-[1380px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
          <div className="lg:col-span-8">
            <SectionLabel index="01" label="The two problems Sentinel solves" />
            <h2 className="mt-6 font-display font-bold text-[#090a0f] text-4xl sm:text-5xl lg:text-[68px] tracking-tight leading-[1.02]">
              Two problems.<br />
              <span className="text-[#52525b]">One intelligent overlay.</span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[#52525b] text-[17px] leading-relaxed">
              Everything else in the product — how we deploy, how we
              prove ourselves, how we keep regulators comfortable — is
              built to make these two outcomes defensible.
            </p>
          </div>
        </div>

        <EditorialBanner
          variant="light"
          gradient="paper-violet"
          image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=2400&q=80"
          kicker="The two outcomes"
          headline="False-positive reduction, and missed risk reduction."
          chips={[
            { c: "#7c3aed", t: "AML + Fraud" },
            { c: "#10b981", t: "ER · Full context" },
            { c: "#f59e0b", t: "Live < 3 mo" },
          ]}
          testId="two-problems-banner"
        />

        {/* The two big cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
          {/* Problem 1 — False Positive Reduction */}
          <div
            data-testid="problem-fp-reduction"
            className="bg-white p-8 lg:p-12 flex flex-col gap-7 min-h-[460px] relative"
          >
            <span
              className="absolute top-0 left-0 right-0 h-[4px]"
              style={{ backgroundColor: "#7c3aed" }}
            />
            <div className="flex items-center justify-between">
              <span
                className="font-mono text-[10px] tracking-[0.22em] uppercase px-2.5 py-1.5"
                style={{ color: "#7c3aed", backgroundColor: "rgba(124,58,237,0.10)" }}
              >
                Problem 01
              </span>
              <span
                className="inline-flex items-center justify-center w-12 h-12"
                style={{ backgroundColor: "rgba(124,58,237,0.10)" }}
              >
                <Filter className="w-6 h-6" style={{ color: "#7c3aed" }} strokeWidth={2} />
              </span>
            </div>

            <div>
              <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#52525b]">
                FPRE &middot; FFPRE
              </p>
              <p className="mt-2 font-display font-bold text-[#090a0f] text-[34px] lg:text-[40px] tracking-tight leading-[1.02]">
                False-positive reduction <br />
                <span className="text-[#52525b]">for AML and fraud alerts.</span>
              </p>
            </div>

            <p className="text-[#52525b] text-[15.5px] leading-relaxed max-w-[52ch]">
              Your AML and fraud analysts are buried in alerts &mdash; most of
              which are false. Sentinel triages each alert with cross-source
              context and a calibrated FP confidence band, with the rationale
              and the citations already on the page.
            </p>

            {/* Outcome metrics */}
            <div className="grid grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
              <div className="bg-white p-4">
                <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#a1a1aa]">
                  Per alert
                </p>
                <p
                  className="mt-2 font-display font-bold text-[26px] tabular tracking-tight leading-none"
                  style={{ color: "#7c3aed" }}
                >
                  ~5 min
                </p>
                <p className="mt-1 text-[#52525b] text-[11px]">vs. ~30 min</p>
              </div>
              <div className="bg-white p-4">
                <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#a1a1aa]">
                  Engines
                </p>
                <p
                  className="mt-2 font-display font-bold text-[26px] tracking-tight leading-none"
                  style={{ color: "#7c3aed" }}
                >
                  2
                </p>
                <p className="mt-1 text-[#52525b] text-[11px]">FPRE + FFPRE</p>
              </div>
              <div className="bg-white p-4">
                <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#a1a1aa]">
                  Mode
                </p>
                <p
                  className="mt-2 font-display font-bold text-[16px] tracking-tight leading-none"
                  style={{ color: "#7c3aed" }}
                >
                  Advisory
                </p>
                <p className="mt-1 text-[#52525b] text-[11px]">Officer decides</p>
              </div>
            </div>
          </div>

          {/* Problem 2 — Missed Risk */}
          <div
            data-testid="problem-entity-resolution"
            className="bg-white p-8 lg:p-12 flex flex-col gap-7 min-h-[460px] relative"
          >
            <span
              className="absolute top-0 left-0 right-0 h-[4px]"
              style={{ backgroundColor: "#10b981" }}
            />
            <div className="flex items-center justify-between">
              <span
                className="font-mono text-[10px] tracking-[0.22em] uppercase px-2.5 py-1.5"
                style={{ color: "#10b981", backgroundColor: "rgba(16,185,129,0.10)" }}
              >
                Problem 02
              </span>
              <span
                className="inline-flex items-center justify-center w-12 h-12"
                style={{ backgroundColor: "rgba(16,185,129,0.10)" }}
              >
                <AlertOctagon className="w-6 h-6" style={{ color: "#10b981" }} strokeWidth={2} />
              </span>
            </div>

            <div>
              <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#52525b]">
                Missed risk &middot; no ER, no full context
              </p>
              <p className="mt-2 font-display font-bold text-[#090a0f] text-[34px] lg:text-[40px] tracking-tight leading-[1.02]">
                Missed risk<br />
                <span className="text-[#52525b]">that hides between your systems.</span>
              </p>
            </div>

            <p className="text-[#52525b] text-[15.5px] leading-relaxed max-w-[52ch]">
              Today, a real customer can sit across multiple CIFs in your
              universal bank, across phase-2/3 M&amp;A duplicates, across
              cross-jurisdictional subsidiaries, across the UBO networks
              behind your corporates, and across BaaS tenants &mdash; and
              every compliance silo only sees a fragment. The risk that
              would be obvious with full context is, today, invisible.
              Sentinel surfaces it.
            </p>

            {/* Outcome metrics */}
            <div className="grid grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
              <div className="bg-white p-4">
                <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#a1a1aa]">
                  Surfaces
                </p>
                <p
                  className="mt-2 font-display font-bold text-[16px] tracking-tight leading-none"
                  style={{ color: "#10b981" }}
                >
                  5 patterns
                </p>
                <p className="mt-1 text-[#52525b] text-[11px]">CIF · M&amp;A · X-juris · UBO · BaaS</p>
              </div>
              <div className="bg-white p-4">
                <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#a1a1aa]">
                  Isolation
                </p>
                <p
                  className="mt-2 font-display font-bold text-[16px] tracking-tight leading-none"
                  style={{ color: "#10b981" }}
                >
                  Per-source
                </p>
                <p className="mt-1 text-[#52525b] text-[11px]">No data wall crossed</p>
              </div>
              <div className="bg-white p-4">
                <p className="font-mono text-[10px] tracking-[0.16em] uppercase text-[#a1a1aa]">
                  Migration
                </p>
                <p
                  className="mt-2 font-display font-bold text-[16px] tracking-tight leading-none"
                  style={{ color: "#10b981" }}
                >
                  None
                </p>
                <p className="mt-1 text-[#52525b] text-[11px]">No CIF change</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-3">
          <a
            href="/get-started?type=diagnostic"
            data-testid="two-problems-cta-diagnostic"
            className="btn-primary-light"
          >
            See it on your own data <ArrowUpRight className="w-4 h-4" />
          </a>
          <a
            href="#features"
            data-testid="two-problems-cta-features"
            className="btn-ghost-light"
          >
            What makes this defensible
          </a>
        </div>
      </div>
    </section>
  );
};

export default TwoProblems;
